import React from "react";
import ReactQuill from "react-quill";
import {
  linkifyPlainTextUrlsInHtml,
  sanitizeRichHtml,
} from "../../utils/richHtmlSanitizer";
import "react-quill/dist/quill.snow.css";
import "./RichEditor.css"; // Import custom CSS file

type RichEditorProps = {
  content: string;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  editable: boolean;
};

export default function RichEditor({
  content,
  setContent,
  editable,
}: RichEditorProps) {
  // Toolbar configuration (only used when `editable` is true)
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image", "video"],
      [{ color: [] }, { background: [] }],
      [{ "code-block": true }],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "image",
    "video",
    "code-block",
  ];

  // Sanitize rendered HTML content to prevent XSS attacks.
  // In read mode, also turn plain pasted URLs into safe clickable links.
  const sanitizedContent = editable
    ? sanitizeRichHtml(content)
    : linkifyPlainTextUrlsInHtml(content);

  return (
    <div className="flex bg-white mt-1 w-full rounded-md">
      <div className="w-full min-h-48 h-full">
        {editable ? (
          // Render ReactQuill with toolbar when `editable` is true
          <ReactQuill
            style={{
              border: "none",
              padding: 0,
              backgroundColor: "transparent",
            }}
            readOnly={false}
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
          />
        ) : (
          // Render a sanitized div with the content when `editable` is false
          <div
            className="ql-editor"
            style={{
              border: "none",
              padding: 0,
              backgroundColor: "transparent",
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}
      </div>
    </div>
  );
}
